import { Component, DestroyRef, effect, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl } from "@angular/forms";
import { catchError, finalize, Observable, of, Subject, switchMap } from "rxjs";
import { PageEvent } from "@angular/material/paginator";
import { DashboardType, TopBarWrapperComponent } from "../top-bar-wrapper/top-bar-wrapper.component";
import { OverviewTilesComponent } from "../../shared/components/overview-tiles/overview-tiles.component";
import { WidgetContainerComponent } from "../my-brand-screen/widget-container/widget-container.component";
import { PieChartComponent } from "../../shared/components/charts/content-category-breakdown/pie-chart.component";
import { PerformanceOverTimeComponent } from "../../shared/components/charts/performance-over-time/performance-over-time.component";
import { SelectItem, SelectMode } from "../../shared/components/select-menu/select-menu.component";
import { DashboardFilters, DashboardService, SocialDashboardFilters } from "../../services/dashboard.service";
import { SocialOverviewScreenService } from "./social-overview-screen.service";
import { AdTypeDistribution, BrandInGallery, ChannelDistribution, CreativeGalleryPayload, CreativeGalleryResponse, SocialOverviewApiResponse, SocialOverviewPayload, SocialOverviewPerformancePayload, SocialOverviewPerformanceResponse, SocialOverviewResponse, SocialOverviewSov } from "./types";
import { ChannelDistributionComponent } from "./channel-distribution/channel-distribution.component";
import { CreativeGalleryComponent } from "../my-brand-screen/creative-gallery/creative-gallery.component";
import { PaginatorComponent } from "../../shared/components/paginator/paginator.component";
import { AdTypeDistributionComponent } from "./ad-type-distribution/ad-type-distribution.component";
import { ChartData } from "../../shared/types/common";
import { PerformanceComparisonMetric } from "../my-brand-screen/types";
import { Format } from "../../services/util.service";
import { SHARE_OF_VOICE_WIDGET_TITLE } from "../my-campaigns-screen/types";
import { MyCompetitionPerformanceWidgetResponse } from "../my-competition-screen/types";
import {WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN} from '../copilot/types';
import { tooltips } from '../../utils/tooltips';

@Component({
    selector: 'app-social-overview-screen-a',
    imports: [
        TopBarWrapperComponent,
        OverviewTilesComponent,
        WidgetContainerComponent,
        PieChartComponent,
        PerformanceOverTimeComponent,
        ChannelDistributionComponent,
        AdTypeDistributionComponent,
        CreativeGalleryComponent,
        PaginatorComponent,
    ],
    templateUrl: './social-overview-screen.component.html',
    styleUrl: './social-overview-screen.component.scss',
})
export class SocialOverviewScreenAComponent implements OnInit {
    protected readonly DashboardType = DashboardType;
    protected readonly SelectMode = SelectMode;
    protected readonly SHARE_OF_VOICE_WIDGET_TITLE = SHARE_OF_VOICE_WIDGET_TITLE;
    protected readonly WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN = WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN;
    protected readonly tooltips = tooltips.socialOverview;

    protected overview: SocialOverviewResponse | undefined;
    protected loadingOverviewDashboard = false;

    protected channelDistribution: ChannelDistribution | undefined;

    protected adType: AdTypeDistribution | undefined;

    protected sovData: ChartData[] = [];
    protected sovColors: string[] = [];

    protected creativeGalleryData: BrandInGallery[] = [];
    protected brandIconMap: Record<string, string> = {};
    protected loadingCreativeGallery = false;
    protected totalSocialCards = 0;
    protected currentGalleryPage = 0;
    protected readonly GALLERY_PAGE_SIZE = 8;
    protected gallerySortControl = new FormControl<string>('impressions_desc', { nonNullable: true });
    protected readonly gallerySortOptions: SelectItem[] = [
        { key: 'impressions_desc', label: 'Impressions (Descending)' },
        { key: 'impressions_asc',  label: 'Impressions (Ascending)' },
        { key: 'cpm_desc',         label: 'CPM (Descending)' },
        { key: 'cpm_asc',          label: 'CPM (Ascending)' },
    ];

    protected loadingPerformanceOverTime = false;
    protected performanceData: MyCompetitionPerformanceWidgetResponse | undefined;
    protected performanceDataIsUnderDateThreshold = false;
    protected performanceOverTimeFormat: Format = 'shortNumber';
    protected performanceMetricControl = new FormControl<string>(PerformanceComparisonMetric.SPEND, { nonNullable: true });
    protected readonly performanceMetricOptions: SelectItem[] = [
        { key: PerformanceComparisonMetric.SPEND, label: 'Spend' },
        { key: PerformanceComparisonMetric.IMPRESSIONS, label: 'Impressions' }
    ];

    private overviewData$ = new Subject<SocialOverviewPayload>();
    private performanceOverTimeData$ = new Subject<SocialOverviewPerformancePayload>();
    private creativeGalleryData$ = new Subject<CreativeGalleryPayload>();
    private destroyRef = inject(DestroyRef);
    protected dashboardService = inject(DashboardService);
    private socialOverviewService = inject(SocialOverviewScreenService);
    protected brandColorsMap = this.dashboardService.brandColorsMap;
    protected brandLineColorsMap = this.dashboardService.brandLineColorsMap;

    constructor() {
        effect(() => {
            const socialDashboardFilters = this.dashboardService.socialBrandsFilters();
            this.initializeData(socialDashboardFilters);
        });
    }

    ngOnInit(): void {
        this.initOverviewSubject().subscribe((result: SocialOverviewApiResponse | undefined) => {
            const filters = this.dashboardService.brandsFilters();
            const allBrands = [filters.myBrand, ...filters.myCompetitors];
            this.overview = result?.overview ? {
                ...result.overview,
                brands: (result.overview.brands ?? []).map(brand => ({
                    ...brand,
                    name: allBrands.find(b => b.key === brand.key)?.label ?? brand.name,
                    shareOfVoice: result.sov?.[brand.key]
                }))
            } : undefined;
            this.channelDistribution = this.buildChannelDistribution(result);
            this.adType = result?.adType;
            this.buildSovChartData(result?.sov);
        });
        this.initPerformanceOverTimeSubject().subscribe((result: SocialOverviewPerformanceResponse | undefined) => {
            this.handlePerformanceChartData(result);
            this.performanceOverTimeFormat = this.getPerformanceOverTimeFormat();
        });
        this.initCreativeGallerySubject().subscribe((result: CreativeGalleryResponse | undefined) => {
            const filters = this.dashboardService.brandsFilters();
            const allBrands = [filters.myBrand, ...filters.myCompetitors];
            this.creativeGalleryData = (result?.brands ?? []).map(brand => ({
                ...brand,
                name: allBrands.find(b => b.key === brand.key)?.label ?? brand.name,
            }));
            this.totalSocialCards = result?.total ?? 0;
        });
    }

    protected onPerformanceMetricChange(): void {
        this.performanceOverTimeFormat = this.getPerformanceOverTimeFormat();
        this.performanceOverTimeData$.next(this.getPerformancePayload(this.dashboardService.socialBrandsFilters()));
    }

    protected onGallerySortChange(): void {
        this.currentGalleryPage = 0;
        this.fetchGalleryPage(0);
    }

    protected onGalleryPageChange(event: PageEvent): void {
        this.currentGalleryPage = event.pageIndex;
        this.fetchGalleryPage(event.pageIndex);
    }

    private fetchGalleryPage(pageIndex: number): void {
        const filters = this.dashboardService.socialBrandsFilters();
        const filtersPayload = this.dashboardService.getDashboardFiltersPayload(filters);
        this.creativeGalleryData$.next({
            ...filtersPayload,
            brandKey: filters.myBrand.key,
            competitorsKeys: filters.myCompetitors.map(c => c.key),
            platforms: filters.platforms,
            limit: this.GALLERY_PAGE_SIZE,
            offset: pageIndex * this.GALLERY_PAGE_SIZE,
        });
    }

    private initializeData(socialDashboardFilters: SocialDashboardFilters): void {
        if (!socialDashboardFilters.myBrand.key) return;
        const filtersPayload = this.dashboardService.getDashboardFiltersPayload(socialDashboardFilters);
        const basePayload: SocialOverviewPayload = {
            ...filtersPayload,
            brandKey: socialDashboardFilters.myBrand.key,
            competitorsKeys: socialDashboardFilters.myCompetitors.map(c => c.key),
            platforms: socialDashboardFilters.platforms
        };
        this.brandIconMap = this.buildBrandIconMap(socialDashboardFilters);
        this.currentGalleryPage = 0;
        this.overviewData$.next(basePayload);
        this.performanceOverTimeData$.next(this.getPerformancePayload(socialDashboardFilters));
        this.creativeGalleryData$.next({ ...basePayload, limit: this.GALLERY_PAGE_SIZE, offset: 0 });
    }

    private getPerformancePayload(socialDashboardFilters: SocialDashboardFilters): SocialOverviewPerformancePayload {
        return {
            ...this.dashboardService.getDashboardFiltersPayload(socialDashboardFilters),
            brandKey: socialDashboardFilters.myBrand.key,
            competitorsKeys: socialDashboardFilters.myCompetitors.map(c => c.key),
            platforms: socialDashboardFilters.platforms,
            metric: this.performanceMetricControl.value,
        };
    }

    private initOverviewSubject(): Observable<SocialOverviewApiResponse | undefined> {
        return this.overviewData$.pipe(
            switchMap((payload: SocialOverviewPayload) => {
                this.loadingOverviewDashboard = true;
                return this.socialOverviewService.getSocialOverview(payload).pipe(
                    takeUntilDestroyed(this.destroyRef),
                    finalize(() => {
                        this.loadingOverviewDashboard = false;
                    }),
                    catchError(() => {
                        this.overview = undefined;
                        return of(undefined);
                    })
                );
            })
        );
    }

    private initPerformanceOverTimeSubject(): Observable<SocialOverviewPerformanceResponse | undefined> {
        return this.performanceOverTimeData$.pipe(
            switchMap((payload: SocialOverviewPerformancePayload) => {
                this.loadingPerformanceOverTime = true;
                return this.socialOverviewService.getSocialOverviewPerformance(payload).pipe(
                    takeUntilDestroyed(this.destroyRef),
                    finalize(() => {
                        this.loadingPerformanceOverTime = false;
                    }),
                    catchError(() => {
                        this.performanceData = undefined;
                        return of(undefined);
                    })
                );
            })
        );
    }

    private initCreativeGallerySubject(): Observable<CreativeGalleryResponse | undefined> {
        return this.creativeGalleryData$.pipe(
            switchMap((payload: CreativeGalleryPayload) => {
                this.loadingCreativeGallery = true;
                return this.socialOverviewService.getCreativeGallery(payload).pipe(
                    takeUntilDestroyed(this.destroyRef),
                    finalize(() => {
                        this.loadingCreativeGallery = false;
                    }),
                    catchError(() => {
                        this.creativeGalleryData = [];
                        return of(undefined);
                    })
                );
            })
        );
    }

    private buildBrandIconMap(filters: DashboardFilters): Record<string, string> {
        return [filters.myBrand, ...filters.myCompetitors].reduce((map, b) => {
            if (b.icon) map[b.key] = b.icon;
            return map;
        }, {} as Record<string, string>);
    }

    private handlePerformanceChartData(result?: SocialOverviewPerformanceResponse): void {
        this.performanceDataIsUnderDateThreshold = result?.isUnderDateThreshold || false;
        if (!result?.myBrand) {
            this.performanceData = undefined;
            return;
        }
        const dashboardFilters = this.dashboardService.brandsFilters();
        this.performanceData = {
            isUnderDateThreshold: result.isUnderDateThreshold,
            myBrand: {
                brandKey: result.myBrand.key,
                brandName: dashboardFilters.myBrand.label,
                dailyPerformance: result.myBrand.dailyPerformance,
            },
            myCompetitors: result.myCompetitors?.map(item => ({
                brandKey: item.key,
                brandName: dashboardFilters.myCompetitors.find(c => c.key === item.key)?.label,
                dailyPerformance: item.dailyPerformance,
            })),
        };
    }

    private getPerformanceOverTimeFormat(): Format {
        switch (this.performanceMetricControl.value) {
            case PerformanceComparisonMetric.SPEND: return 'shortCurrencyNumber';
            case PerformanceComparisonMetric.IMPRESSIONS: return 'shortNumber';
            default: return 'shortNumber';
        }
    }

    private buildChannelDistribution(result: SocialOverviewApiResponse | undefined): ChannelDistribution | undefined {
        if (!result?.channelDistribution) return undefined;
        const filters = this.dashboardService.brandsFilters();
        const allBrands = [filters.myBrand, ...filters.myCompetitors];
        return {
            brands: result.channelDistribution.brands.map(brand => ({
                ...brand,
                name: allBrands.find(b => b.key === brand.key)?.label ?? brand.name,
            })),
        };
    }

    private buildSovChartData(sov: SocialOverviewSov | undefined): void {
        if (!sov) {
            this.sovData = [];
            this.sovColors = [];
            return;
        }
        const entries = Object.entries(sov);
        if (entries.every(([, share]) => share === 0)) {
            this.sovData = [];
            this.sovColors = [];
            return;
        }
        const filters = this.dashboardService.brandsFilters();
        const allBrands = [filters.myBrand, ...filters.myCompetitors];
        this.sovData = entries.map(([key, share]) => ({
            key,
            name: allBrands.find(b => b.key === key)?.label ?? key,
            share,
        }));
        this.sovColors = this.sovData.map(item => this.brandColorsMap()[item.key as string]);
    }

}
